import React, { useState } from "react";
import axios from "axios";
import { ArrowRight, Sparkles, Loader2, CheckCircle2, AlertCircle, TrendingUp } from "lucide-react";
import Modal from "@/components/Modal";
import { useLang } from "@/contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COPY = {
  bg: {
    eyebrow: "БЕЗПЛАТЕН AI AUDIT — ОГРАНИЧЕНИ МЕСТА",
    title: "Получи безплатен 5-минутен audit на ",
    titleAccent: "твоя бранд",
    sub: "Въведи URL на сайта или Instagram. AI ще анализира SEO, скорост, конверсия и social присъствие. Резултат в реално време.",
    placeholder: "yoursite.com или @brand",
    cta: "Стартирай audit",
    analyzing: "Анализираме сайта...",
    successTitle: "Audit готов",
    successSub: "Ето твоят персонализиран отчет. Изпратихме копие на имейла ти. Един от нашите стратези ще се свърже в рамките на 24ч.",
    perks: [
      "SEO score & top 3 проблема",
      "Performance & speed insights",
      "Social presence анализ",
      "3 quick-wins препоръки",
    ],
    field: "Имейл за отчета",
    emailPlaceholder: "you@brand.com",
    close: "Затвори",
    seo: "SEO",
    perf: "Скорост",
    social: "Social",
    overall: "Общ резултат",
    summaryLabel: "Резюме",
    issuesLabel: "Топ 3 проблема",
    winsLabel: "3 Quick Wins",
    error: "Нещо се обърка. Опитай пак.",
  },
  en: {
    eyebrow: "FREE AI AUDIT — LIMITED SLOTS",
    title: "Get a free 5-minute audit of ",
    titleAccent: "your brand",
    sub: "Enter your website URL or Instagram handle. Our AI analyzes SEO, speed, conversion, and social presence. Real-time result.",
    placeholder: "yoursite.com or @brand",
    cta: "Run the audit",
    analyzing: "Analyzing your site...",
    successTitle: "Audit ready",
    successSub: "Here's your custom report. We've sent a copy to your inbox. One of our strategists will reach out within 24h.",
    perks: [
      "SEO score & top 3 issues",
      "Performance & speed insights",
      "Social presence analysis",
      "3 quick-win recommendations",
    ],
    field: "Email for the report",
    emailPlaceholder: "you@brand.com",
    close: "Close",
    seo: "SEO",
    perf: "Speed",
    social: "Social",
    overall: "Overall",
    summaryLabel: "Summary",
    issuesLabel: "Top 3 issues",
    winsLabel: "3 Quick Wins",
    error: "Something went wrong. Try again.",
  },
};

const SeverityBadge = ({ level, lang }) => {
  const map = {
    high: { bg: "bg-brand-pink/15 border-brand-pink/40 text-brand-pinkLight", labelBg: "ВИСОКА", labelEn: "HIGH" },
    medium: { bg: "bg-brand-purple/15 border-brand-purple/40 text-brand-purpleLight", labelBg: "СРЕДНА", labelEn: "MED" },
    low: { bg: "bg-brand-teal/15 border-brand-teal/40 text-brand-tealLight", labelBg: "НИСКА", labelEn: "LOW" },
  };
  const c = map[level] || map.medium;
  return (
    <span className={`px-2 py-0.5 rounded-full text-[9px] font-mono uppercase tracking-[0.2em] border ${c.bg}`}>
      {lang === "bg" ? c.labelBg : c.labelEn}
    </span>
  );
};

const ScoreRing = ({ value, label }) => {
  const pct = Math.max(0, Math.min(100, value || 0));
  const circ = 2 * Math.PI * 28;
  const offset = circ - (pct / 100) * circ;
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="relative w-20 h-20">
        <svg viewBox="0 0 64 64" className="w-full h-full -rotate-90">
          <circle cx="32" cy="32" r="28" stroke="rgba(168,85,247,0.15)" strokeWidth="4" fill="none" />
          <circle
            cx="32" cy="32" r="28"
            stroke="url(#scoreGrad)" strokeWidth="4" fill="none"
            strokeLinecap="round"
            strokeDasharray={circ}
            strokeDashoffset={offset}
            style={{ transition: "stroke-dashoffset 1s ease-out" }}
          />
          <defs>
            <linearGradient id="scoreGrad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#A855F7" />
              <stop offset="50%" stopColor="#EC4899" />
              <stop offset="100%" stopColor="#34D399" />
            </linearGradient>
          </defs>
        </svg>
        <span className="absolute inset-0 grid place-items-center font-heading text-lg font-black">
          {pct}
        </span>
      </div>
      <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-400">{label}</span>
    </div>
  );
};

const AuditModal = ({ open, onClose }) => {
  const { lang } = useLang();
  const c = COPY[lang];
  const [step, setStep] = useState(0); // 0 input, 1 loading, 2 done, 3 error
  const [target, setTarget] = useState("");
  const [email, setEmail] = useState("");
  const [report, setReport] = useState(null);
  const [errMsg, setErrMsg] = useState("");

  const reset = () => {
    setStep(0);
    setTarget("");
    setEmail("");
    setReport(null);
    setErrMsg("");
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!target || !email) return;
    setStep(1);
    setErrMsg("");
    try {
      const { data } = await axios.post(`${API}/leads/audit`, { target, email, lang }, { timeout: 25000 });
      setReport(data);
      setStep(2);
    } catch (err) {
      setErrMsg(err?.response?.data?.detail || c.error);
      setStep(3);
    }
  };

  return (
    <Modal open={open} onClose={handleClose} testId="audit-modal" maxWidth="max-w-3xl">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="aurora opacity-50" />
      </div>

      <div className="relative p-8 md:p-12 max-h-[85vh] overflow-y-auto">
        {step === 0 && (
          <>
            <div className="flex items-center gap-2 mb-5">
              <Sparkles className="w-4 h-4 text-brand-purpleLight" />
              <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-brand-purpleLight">
                {c.eyebrow}
              </p>
            </div>
            <h3 className="font-heading text-3xl md:text-5xl font-black leading-[1] tracking-tighter mb-5">
              {c.title}<span className="text-white">{c.titleAccent}</span>
            </h3>
            <p className="text-zinc-300 text-sm md:text-base leading-relaxed max-w-xl mb-8">
              {c.sub}
            </p>

            <form onSubmit={submit} className="space-y-4" data-testid="audit-form">
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-400 mb-2">
                  {lang === "bg" ? "Сайт или Instagram" : "Website or Instagram"}
                </label>
                <input
                  data-testid="audit-target" required
                  value={target} onChange={(e) => setTarget(e.target.value)}
                  placeholder={c.placeholder}
                  className="w-full bg-brand-bg/60 border border-brand-border focus:border-brand-purple focus:outline-none px-4 py-4 text-white placeholder-zinc-500 transition-colors rounded-xl"
                />
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-400 mb-2">
                  {c.field}
                </label>
                <input
                  data-testid="audit-email" required type="email"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder={c.emailPlaceholder}
                  className="w-full bg-brand-bg/60 border border-brand-border focus:border-brand-purple focus:outline-none px-4 py-4 text-white placeholder-zinc-500 transition-colors rounded-xl"
                />
              </div>
              <button
                data-testid="audit-submit" type="submit"
                className="w-full inline-flex items-center justify-center gap-2 btn-holo text-white font-bold uppercase tracking-wide px-6 py-5 rounded-xl text-sm"
              >
                {c.cta}
                <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
              </button>
            </form>

            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-zinc-300">
              {c.perks.map((p, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="mt-[6px] w-1.5 h-1.5 rounded-full bg-brand-purpleLight shrink-0" />
                  {p}
                </li>
              ))}
            </ul>
          </>
        )}

        {step === 1 && (
          <div className="py-12 flex flex-col items-center text-center">
            <div className="relative w-24 h-24 mb-6">
              <div className="absolute inset-0 rounded-full border-2 border-brand-purple/30" />
              <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-brand-pink animate-spin" />
              <Loader2 className="absolute inset-0 m-auto w-8 h-8 text-brand-purpleLight animate-pulse" />
            </div>
            <p className="font-heading text-2xl md:text-3xl font-bold tracking-tight">
              {c.analyzing}
            </p>
            <p className="mt-3 font-mono text-xs uppercase tracking-[0.25em] text-zinc-500">
              SEO • PERF • SOCIAL • CONVERSION
            </p>
          </div>
        )}

        {step === 2 && report && (
          <div data-testid="audit-success" className="space-y-6">
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle2 className="w-6 h-6 text-brand-tealLight" />
              <h3 className="font-heading text-2xl md:text-3xl font-black tracking-tighter">
                {c.successTitle}
              </h3>
            </div>
            <p className="text-zinc-300 text-sm leading-relaxed">{c.successSub}</p>

            {/* Score rings */}
            <div className="grid grid-cols-4 gap-3 py-4 border-y border-brand-border">
              <ScoreRing value={report.overall_score} label={c.overall} />
              <ScoreRing value={report.seo_score} label={c.seo} />
              <ScoreRing value={report.performance_score} label={c.perf} />
              <ScoreRing value={report.social_score} label={c.social} />
            </div>

            {/* Summary */}
            {report.summary && (
              <div>
                <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-brand-purpleLight mb-2">
                  {c.summaryLabel}
                </p>
                <p className="text-sm text-zinc-200 leading-relaxed">{report.summary}</p>
              </div>
            )}

            {/* Issues */}
            {report.top_issues?.length > 0 && (
              <div>
                <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-brand-pinkLight mb-3">
                  {c.issuesLabel}
                </p>
                <ul className="space-y-3">
                  {report.top_issues.map((it, i) => (
                    <li key={i} className="rounded-xl border border-brand-border bg-brand-surface/50 p-4">
                      <div className="flex items-center justify-between mb-2 gap-3">
                        <p className="font-bold text-white text-sm">{it.title}</p>
                        <SeverityBadge level={it.severity} lang={lang} />
                      </div>
                      <p className="text-xs text-zinc-400 leading-relaxed">{it.detail}</p>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Quick wins */}
            {report.quick_wins?.length > 0 && (
              <div>
                <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-brand-tealLight mb-3 inline-flex items-center gap-2">
                  <TrendingUp className="w-3 h-3" /> {c.winsLabel}
                </p>
                <ul className="space-y-3">
                  {report.quick_wins.map((it, i) => (
                    <li key={i} className="rounded-xl border border-brand-teal/30 bg-brand-teal/5 p-4">
                      <p className="font-bold text-white text-sm mb-1">{it.title}</p>
                      <p className="text-xs text-zinc-400 leading-relaxed">{it.detail}</p>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <button
              data-testid="audit-close-success" onClick={handleClose}
              className="w-full inline-flex items-center justify-center gap-2 btn-outline-glow text-white font-bold uppercase tracking-wide px-6 py-4 rounded-xl text-xs"
            >
              {c.close}
            </button>
          </div>
        )}

        {step === 3 && (
          <div className="py-10 text-center" data-testid="audit-error">
            <AlertCircle className="w-12 h-12 mx-auto text-brand-pink mb-4" />
            <p className="font-heading text-2xl font-bold mb-2">{c.error}</p>
            <p className="text-sm text-zinc-400 mb-6">{errMsg}</p>
            <button
              onClick={() => setStep(0)}
              className="inline-flex items-center gap-2 btn-outline-glow text-white font-bold uppercase tracking-wide px-6 py-4 rounded-xl text-xs"
            >
              {lang === "bg" ? "Опитай пак" : "Try again"}
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default AuditModal;
